import React from 'react';
import { Badge, Text } from '@mantine/core';
import {
    IconBellFilled,
    IconClockHour8,
    IconMapPin,
} from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { When } from '../common';
import type { ActionCardItemDto } from '../../codegen/graphql';
import { SurveySourceBadge } from './SurveySourceBadge';
import { PriceChangeScopeBadge } from './PriceChangeScopeBadge';
import {
    getRequestLocationName,
    hasRequestAdvertisingZoneName,
} from './requestLocation';

type ActionCardItemData = ActionCardItemDto;

type Props = {
    action: ActionCardItemData;
    hasBorder?: boolean;
};

function ActionCardItem({ action, hasBorder }: Props) {
    const [, setSearchParams] = useSearchParams();

    const hasZoneName = hasRequestAdvertisingZoneName(
        action.advertisingZoneName,
    );
    const locationName = getRequestLocationName({
        advertisingZoneName: action.advertisingZoneName,
        storeName: action.storeName,
    });

    const textColor =
        !action?.completed &&
        dayjs(action.dueBy).isBefore(dayjs().add(4, 'hours'))
            ? 'text-red-500'
            : 'text-primary';

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus
        <div
            className={`flex items-center gap-4 w-full p-3 py-5 hover:bg-gray-50 cursor-pointer ${
                hasBorder ? 'border-b border-gray-200' : ''
            }`}
            role="button"
            onClick={() =>
                setSearchParams({
                    requestId: action.priceChangeRequestId,
                })
            }
        >
            <div className="rounded-full bg-red-500/20 p-2 aspect-square flex items center justify-center text-red-500">
                <IconBellFilled />
            </div>
            <div className="flex flex-1 flex-col gap-1">
                <div className="flex gap-4 items-center flex-wrap">
                    <Badge variant="light">
                        {action.productCategoryName || 'N/A'}
                    </Badge>
                    <When condition={!!action.isFromPriceSurvey}>
                        <SurveySourceBadge
                            isFromPriceSurvey={action.isFromPriceSurvey}
                            priceSurveyType={
                                (action as { priceSurveyType?: string })
                                    .priceSurveyType
                            }
                            priceSurveyLabel={
                                (action as { priceSurveyLabel?: string })
                                    .priceSurveyLabel
                            }
                            size="md"
                        />
                    </When>
                    <PriceChangeScopeBadge
                        request={action}
                        size="md"
                        descriptive
                    />
                    <div className="flex gap-2 items-center">
                        <IconMapPin size={18} />
                        <Text size="sm">{locationName}</Text>
                        <When condition={!hasZoneName}>
                            <Badge size="sm" color="gray" variant="light">
                                {action.storeNo || 'N/A'}
                            </Badge>
                        </When>
                    </div>
                </div>
                <div className="flex w-full overflow-hidden items-center justify-between">
                    <div className="flex items-center gap-5">
                        <Text className="text-base font-bold text-[#43474E]">
                            # {action.productSku?.replace(/^0+/, '') || 'N/A'}
                        </Text>
                        <Text className="text-xs text-[#43474E] whitespace-nowrap text-ellipsis">
                            {action.productDescription}
                        </Text>
                    </div>
                    <div className={`flex items-center gap-2 ${textColor}`}>
                        <IconClockHour8 size={18} />
                        <Text size="sm" className="capitalize">
                            Due {dayjs(action.dueBy).fromNow()}
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ActionCardItem;
